import type {
  AudioConversionDetail,
  AudioConversionSummary,
  ChapterBbox as ChapterBboxSchema,
  ChapterDetail as ChapterDetailSchema,
} from './schemas';
import {
  VoiceSummary,
  knownVoiceDisplayName,
  knownVoiceModelId,
} from './schemas';
import { assertDefined } from '@listening/utils/ts-utils';

// Canonical audio types are the Effect HttpApi schema types — collections,
// services, and components all share one source of truth so no `as unknown as`
// bridging is needed. (Name kept for now; consumers can import from
// `@listening/api-contract/schemas` directly as the legacy surface is retired.)
export type AudioItemSummary = typeof AudioConversionSummary.Type;
export type AudioItemDetail = AudioConversionDetail;
// `Voice` is the canonical VOICE_DEFS-derived schema type — NOT the
// staging-generated `components['schemas']['VoiceSummary']` snapshot — so a
// voice rename/removal is a compile error in every consumer of this type.
export type Voice = VoiceSummary;

// Default voices are typed as the canonical `Voice`, so `name`/`model_id` are
// checked against the VOICE_DEFS-derived unions: renaming "Will"/"am_liam" or
// "Scarlett"/"af_heart" fails typecheck here.
export const defaultMaleVoice: Voice = {
  gender: 'male',
  image_url: '/voice-images/am_liam.webp',
  is_free: true,
  lang: 'en',
  model_id: knownVoiceModelId('am_liam'),
  name: knownVoiceDisplayName('Will'),
  sample_url: '/voice-samples/am_liam.mp3',
  softness: 'moderate',
};

export const defaultFemaleVoice: Voice = {
  gender: 'female',
  image_url: '/voice-images/af_heart.webp',
  is_free: true,
  lang: 'en',
  model_id: knownVoiceModelId('af_heart'),
  name: knownVoiceDisplayName('Scarlett'),
  sample_url: '/voice-samples/af_heart.mp3',
  softness: 'moderate',
};

export type StrictOmit<T, K extends keyof T> = Omit<T, K>;

export type Chapter = {
  id: string;
  title: string;
  paragraphs: Array<Paragraph>;
};

export type Paragraph = {
  sentences: Array<Sentence>;
  bboxes: Array<Bbox>;
};

export type Sentence = {
  words: Array<Word>;
  bboxes: Array<Bbox>;
};

export type Word = {
  text: string;
  chunkId: string;
  wordIdxInChunk: number;
  bboxes: Array<Bbox>;
};

export type Bbox = {
  bbox: [number, number, number, number]; // x1, y1, x2, y2
  page: number;
};

type ChapterBboxDto = typeof ChapterBboxSchema.Type;

const bboxEquals = (bbox1: ChapterBboxDto, bbox2: ChapterBboxDto) =>
  bbox1.page === bbox2.page &&
  bbox1.bbox[0] === bbox2.bbox[0] &&
  bbox1.bbox[1] === bbox2.bbox[1] &&
  bbox1.bbox[2] === bbox2.bbox[2] &&
  bbox1.bbox[3] === bbox2.bbox[3];

// The Effect chapter schema yields readonly bbox tuples; the doc-reader model
// uses mutable Bbox. Copy each into a fresh mutable Bbox at the boundary.
const toBbox = (b: ChapterBboxDto): Bbox => ({
  page: b.page,
  bbox: [b.bbox[0], b.bbox[1], b.bbox[2], b.bbox[3]],
});

export type FetchChaptersFnReturnType = {
  data: Array<Chapter>;
  cursor: string | null;
};

export function listeningApiChapterDtoToDocReader(data: {
  chapters: ReadonlyArray<typeof ChapterDetailSchema.Type>;
  cursor: string | null;
}): FetchChaptersFnReturnType {
  let chunkId: string | null = null;
  let wordInChunkCounter = 0;
  return {
    cursor: data.cursor,
    data: data.chapters.map((c) => ({
      id: c.id,
      paragraphs: c.paragraphs.map((p): Paragraph => ({
        bboxes: p.bboxes.map(toBbox),
        sentences: p.sentences.map((s) => ({
          bboxes: s.bboxes.map(toBbox),
          words: s.words.map((w, wIdx): Word => {
            const prevWord = s.words[wIdx - 1];

            if (w.tts_hash_id) {
              chunkId = w.tts_hash_id;
              wordInChunkCounter = 0;
            } else {
              wordInChunkCounter += 1;
            }

            return {
              // Some things in the document such as: "$1,000" are split into multiple words (thousand, dollars)
              // but we want to treat them as a single bbox
              bboxes: w.bboxes
                .filter(
                  (bbox) =>
                    !prevWord?.bboxes.some((prevBbox) =>
                      bboxEquals(prevBbox, bbox),
                    ),
                )
                .map(toBbox),
              chunkId: assertDefined(chunkId),
              text: w.text,
              wordIdxInChunk: wordInChunkCounter,
            };
          }),
        })),
      })),
      title: c.title,
    })),
  };
}
